import * as React from 'react';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Autocomplete, { AutocompleteChangeReason } from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { IRecipientList } from '../../models/communication.types';
import { SyntheticEvent, useEffect, useMemo } from 'react';
import { IPageInfo } from '../../models/page.types';
import { Chip } from '@material-ui/core';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface RecipientsListProps {
  recipients: IRecipientList[];
  onRecipientsChange: (options: IRecipientList[]) => void;
  onScroll: (endReached: boolean) => void;
  onInputChange: (value: string) => void;
  selectedRecipientsList: IRecipientList[];
  pageInfo: IPageInfo;
  allChecked: (value: boolean) => void;
}

export const RecipientsList: React.FC<RecipientsListProps> = (props) => {
  const [allChecked, setAllChecked] = React.useState<boolean>(false);

  const selectAll = {
    userId: '',
    firstName: 'Wszyscy użytkownicy systemu',
    lastName: '',
    email: '',
  };

  const recipientListWitSelectAll: IRecipientList[] = useMemo(() => {
    return [selectAll, ...props.recipients];
  }, [props.recipients]);

  const recipientSelectAll: IRecipientList[] = useMemo(() => {
    return [selectAll];
  }, [props.recipients]);

  useEffect(() => {
    props.allChecked(allChecked);
  }, [allChecked]);

  const handleRecipientsChange = (
    event: SyntheticEvent<Element, Event>,
    value: IRecipientList[],
    reason: AutocompleteChangeReason,
  ) => {
    if (value.find((value) => value.firstName === selectAll.firstName)) {
      setAllChecked(true);
    } else {
      props.onRecipientsChange(value);
      setAllChecked(false);
    }
    if (reason === 'clear') {
      setAllChecked(false);
    }
  };

  return (
    <Autocomplete
      ListboxProps={{
        role: 'list-box',
        onScroll: (event: React.SyntheticEvent) => {
          const listboxNode = event.currentTarget;
          if (Math.ceil(listboxNode.scrollTop + listboxNode.clientHeight) === Math.ceil(listboxNode.scrollHeight)) {
            props.onScroll(true);
          }
        },
      }}
      multiple
      limitTags={allChecked ? 1 : 20}
      id="recipients-checkbox-list"
      filterOptions={(x) => x}
      onInputChange={(e, value, reason) => props.onInputChange(value)}
      options={allChecked ? recipientSelectAll : recipientListWitSelectAll}
      value={allChecked ? recipientSelectAll : props.selectedRecipientsList}
      disableCloseOnSelect
      getOptionLabel={(option) =>
        option.firstName === selectAll.firstName
          ? `Zaznaczono wszystkich ${props.pageInfo.totalCount} użytkowników`
          : `${option.firstName} ${option.lastName} ${option.email}`
      }
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <React.Fragment key={option.email}>
            <Chip
              label={`${option.firstName} ${option.lastName}`}
              {...getTagProps({ index })}
              style={{ color: '#2C2D3E', background: '#FAFAFF', border: '1px solid rgba(44, 45, 62, 0.3' }}
            />
          </React.Fragment>
        ))
      }
      renderOption={(props, option, { inputValue, selected }) =>
        inputValue !== '' && option.firstName === selectAll.firstName ? (
          <></>
        ) : (
          <li {...props}>
            <Checkbox
              disableRipple
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={option.firstName === selectAll.firstName ? allChecked : selected}
            />
            {option.firstName} {option.lastName} {option.email}
          </li>
        )
      }
      renderInput={(params) => <TextField {...params} label="Wybierz" />}
      onChange={handleRecipientsChange}
    />
  );
};
